import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getTimeline,
  createPost,
  deletePost,
  likePost,
  unlikePost,
  normalizeFeedMode,
} from '../services/feedApi';
import { getAuthToken, getAuthUserSnapshot } from '../utils/auth';
import { socket as authenticatedSocket } from '../services/socket';

function toSafeArray(value) {
  return Array.isArray(value) ? value : [];
}



function normalizeSign(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}




function sortPostsByCreatedAt(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const aTime = new Date(a?.createdAt || 0).getTime();
    const bTime = new Date(b?.createdAt || 0).getTime();
    if (bTime !== aTime) return bTime - aTime;
    return String(b?.id || '').localeCompare(String(a?.id || ''));
  });
}

// Generic shared merge helper for stable, unique item merging
function mergeUniqueItemsStable(currentItems, incomingItems, sortItems) {
  const map = new Map();

  [...toSafeArray(currentItems), ...toSafeArray(incomingItems)].forEach((item) => {
    if (!item?.id) return;

    const key = String(item.id);
    const previous = map.get(key);

    if (!previous) {
      map.set(key, item);
      return;
    }

    if (previous?._optimistic && !item?._optimistic) {
      map.set(key, item);
      return;
    }

    if (!previous?._optimistic && item?._optimistic) {
      return;
    }

    const previousTime = new Date(previous?.updatedAt || previous?.createdAt || 0).getTime();
    const nextTime = new Date(item?.updatedAt || item?.createdAt || 0).getTime();

    map.set(key, nextTime >= previousTime ? item : previous);
  });

  return typeof sortItems === 'function'
    ? sortItems(Array.from(map.values()))
    : Array.from(map.values());
}

function mergeUniquePostsStable(currentItems, incomingItems) {
  return mergeUniqueItemsStable(currentItems, incomingItems, sortPostsByCreatedAt);
}

function updatePostInList(items, postId, updater) {
  return items.map((item) => {
    if (String(item?.id || '') !== String(postId || '')) return item;
    return typeof updater === 'function' ? updater(item) : item;
  });
}

function createOptimisticPost({ text = '', viewer = null, imageUpload = null } = {}) {
  const nowIso = new Date().toISOString();
  const tempId = `temp-feed-post-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const safeViewer = viewer && typeof viewer === 'object' ? viewer : {};
  const fullName =
    safeViewer?.fullName ||
    safeViewer?.name ||
    safeViewer?.displayName ||
    'Vos';

  const username =
    safeViewer?.username ||
    safeViewer?.userName ||
    '';

  const zodiacSign =
    safeViewer?.zodiacSign ||
    safeViewer?.sign ||
    safeViewer?.sunSign ||
    '';

  const avatarUrl =
    safeViewer?.avatarUrl ||
    safeViewer?.photoPath ||
    safeViewer?.profilePicture ||
    safeViewer?.profilePhoto ||
    safeViewer?.profileImage ||
    safeViewer?.avatar ||
    safeViewer?.image ||
    safeViewer?.photo ||
    safeViewer?.photoURL ||
    '';

  const authorId = String(safeViewer?.id || safeViewer?._id || '');

  const safeImageUpload = imageUpload && typeof imageUpload === 'object' ? imageUpload : null;
  const hasMedia = !!safeImageUpload?.id;

  return {
    id: tempId,
    text: String(text || '').trim(),
    sign: zodiacSign,
    visibility: 'public',
    status: 'active',
    createdAt: nowIso,
    updatedAt: nowIso,
    editedAt: null,
    author: {
      id: authorId,
      fullName,
      username,
      zodiacSign,
      avatarUrl,
    },
    hasMedia,
    media: {
      kind: hasMedia ? 'image' : 'none',
      image: {
        uploadId: safeImageUpload?.id || null,
        url: String(safeImageUpload?.url || '').trim(),
        thumbUrl: String(safeImageUpload?.thumbUrl || safeImageUpload?.url || '').trim(),
        width:
          safeImageUpload?.width === null || safeImageUpload?.width === undefined
            ? null
            : Number.isFinite(Number(safeImageUpload?.width))
              ? Number(safeImageUpload?.width)
              : null,
        height:
          safeImageUpload?.height === null || safeImageUpload?.height === undefined
            ? null
            : Number.isFinite(Number(safeImageUpload?.height))
              ? Number(safeImageUpload?.height)
              : null,
        blurHash: String(safeImageUpload?.blurHash || '').trim(),
        alt: '',
      },
    },
    stats: {
      likeCount: 0,
      commentCount: 0,
      shareCount: 0,
    },
    viewer: {
      liked: false,
      isOwner: true,
    },
    _optimistic: true,
    _clientRequestKey: tempId,
  };
}


function extractOptimisticPosts(items) {
  return toSafeArray(items).filter((item) => item?._optimistic);
}

function removePostFromList(items, targetId) {
  return toSafeArray(items).filter((item) => String(item?.id || '') !== String(targetId || ''));
}

function reconcileCreatedPost(items, optimisticPost, createdPost) {
  const safeItems = toSafeArray(items);
  const optimisticId = String(optimisticPost?.id || '');
  const createdId = String(createdPost?.id || '');

  if (!createdId) {
    return removePostFromList(safeItems, optimisticId);
  }

  const withoutOptimistic = removePostFromList(safeItems, optimisticId);
  return mergeUniquePostsStable(withoutOptimistic, [createdPost]);
}

function mergeServerPostsPreservingOptimistic(currentItems, incomingItems) {
  const optimistic = extractOptimisticPosts(currentItems);
  return mergeUniquePostsStable(optimistic, Array.isArray(incomingItems) ? incomingItems : []);
}

function getNonOptimisticIds(items) {
  return new Set(
    toSafeArray(items)
      .filter((item) => item?.id && !item?._optimistic)
      .map((item) => String(item.id))
  );
}

function getTrulyNewServerPosts(currentItems, incomingItems) {
  const currentIds = getNonOptimisticIds(currentItems);
  return (Array.isArray(incomingItems) ? incomingItems : []).filter((item) => {
    const id = String(item?.id || '');
    if (!id) return false;
    return !currentIds.has(id);
  });
}

function hasPostId(items, postId) {
  const safePostId = String(postId || '');
  if (!safePostId) return false;

  return (Array.isArray(items) ? items : []).some(
    (item) => String(item?.id || '') === safePostId
  );
}

function postMatchesFeedMode(post, mode, viewerSign) {
  const safeMode = String(mode || 'recientes').trim().toLowerCase();
  if (safeMode === 'para-vos' || safeMode === 'recientes') return true;

  if (safeMode === 'tu-signo') {
    const postSign = normalizeSign(post?.author?.zodiacSign || post?.sign || '');
    return !!viewerSign && postSign === viewerSign;
  }

  return true;
}

function pruneDismissedEntries(map, maxAgeMs) {
  const now = Date.now();
  for (const [id, ts] of map.entries()) {
    if (!id || !ts || now - ts > maxAgeMs) {
      map.delete(id);
    }
  }
  return map;
}

function mergePendingPosts(currentPending, incomingPending, currentItems, dismissedFilter) {
  const staged = mergeUniquePostsStable(currentPending, incomingPending);
  const trulyNew = getTrulyNewServerPosts(currentItems, staged);
  return typeof dismissedFilter === 'function'
    ? dismissedFilter(trulyNew)
    : trulyNew;
}

export default function useFeedTimeline(options = {}) {
  const {
    initialLimit = 10,
    autoLoad = true,
    mode = 'recientes',
  } = options;

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingPost, setCreatingPost] = useState(false);
  const [error, setError] = useState('');
  const [pendingNewItems, setPendingNewItems] = useState([]);
  const [checkingNewPosts, setCheckingNewPosts] = useState(false);
  const [recentlyAppliedPostIds, setRecentlyAppliedPostIds] = useState([]);
  const [lastRefreshAt, setLastRefreshAt] = useState(0);

  const mountedRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const pendingLikeIdsRef = useRef(new Set());
  const dismissedPendingIdsRef = useRef(new Map());
  const dismissedPendingMaxAgeMsRef = useRef(2 * 60 * 1000);

  const socketRef = useRef(null);
  const itemsRef = useRef([]);
  const pendingItemsRef = useRef([]);
  const creatingPostRef = useRef(false);
  const lastLoadedModeRef = useRef('');

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const token = useMemo(() => getAuthToken(), []);
  const viewerSnapshot = useMemo(() => getAuthUserSnapshot(), []);
  const safeMode = useMemo(() => normalizeFeedMode(mode), [mode]);

  const viewerSign = useMemo(
    () => normalizeSign(
      viewerSnapshot?.zodiacSign ||
      viewerSnapshot?.sign ||
      viewerSnapshot?.sunSign ||
      ''
    ),
    [viewerSnapshot]
  );

  const viewerId = useMemo(
    () => String(viewerSnapshot?.id || viewerSnapshot?._id || '').trim(),
    [viewerSnapshot]
  );

  const hasMore = useMemo(() => !!nextCursor, [nextCursor]);
  const pendingNewCount = useMemo(() => pendingNewItems.length, [pendingNewItems]);
  const hasPendingNewPosts = useMemo(() => pendingNewCount > 0, [pendingNewCount]);
  const hasRecentlyAppliedPosts = useMemo(
    () => recentlyAppliedPostIds.length > 0,
    [recentlyAppliedPostIds]
  );

  useEffect(() => {
    itemsRef.current = Array.isArray(items) ? items : [];
  }, [items]);

  useEffect(() => {
    creatingPostRef.current = creatingPost;
  }, [creatingPost]);

  useEffect(() => {
    pendingItemsRef.current = toSafeArray(pendingNewItems);
  }, [pendingNewItems]);


  const filterDismissedPendingPosts = useCallback((itemsToFilter) => {
    const dismissedMap = pruneDismissedEntries(
      dismissedPendingIdsRef.current,
      dismissedPendingMaxAgeMsRef.current
    );

    return (Array.isArray(itemsToFilter) ? itemsToFilter : []).filter((item) => {
      const id = String(item?.id || '');
      if (!id) return false;
      return !dismissedMap.has(id);
    });
  }, []);

  const clearPendingState = useCallback(() => {
    setPendingNewItems([]);
    dismissedPendingIdsRef.current = new Map();
  }, []);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getTimeline({
        token,
        limit: initialLimit,
        mode: safeMode,
      });

      lastLoadedModeRef.current = safeMode;
      if (!mountedRef.current) return;

      setItems((prev) =>
        mergeServerPostsPreservingOptimistic(
          prev,
          Array.isArray(result?.items) ? result.items : []
        )
      );
      clearPendingState();
      setLastRefreshAt(Date.now());
      setNextCursor(result?.nextCursor || null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || 'No se pudo cargar el feed.');
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
    }
  }, [clearPendingState, initialLimit, safeMode, token]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError('');

    try {
      const result = await getTimeline({
        token,
        limit: initialLimit,
        mode: safeMode,
      });

      lastLoadedModeRef.current = safeMode;
      if (!mountedRef.current) return;

      setItems((prev) =>
        mergeServerPostsPreservingOptimistic(
          prev,
          Array.isArray(result?.items) ? result.items : []
        )
      );
      clearPendingState();
      setLastRefreshAt(Date.now());
      setNextCursor(result?.nextCursor || null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || 'No se pudo actualizar el feed.');
    } finally {
      if (!mountedRef.current) return;
      setRefreshing(false);
    }
  }, [clearPendingState, initialLimit, safeMode, token]);

  const checkForNewPosts = useCallback(async () => {
    if (loading || refreshing || creatingPostRef.current || checkingNewPosts) return null;

    setCheckingNewPosts(true);

    try {
      const result = await getTimeline({
        token,
        limit: initialLimit,
        mode: safeMode,
      });

      if (!mountedRef.current) return null;

      const incomingItems = Array.isArray(result?.items) ? result.items : [];

      setPendingNewItems((prev) =>
        mergePendingPosts(prev, incomingItems, itemsRef.current, filterDismissedPendingPosts)
      );

      return incomingItems;
    } catch {
      return null;
    } finally {
      if (!mountedRef.current) return null;
      setCheckingNewPosts(false);
    }
  }, [checkingNewPosts, filterDismissedPendingPosts, initialLimit, loading, refreshing, safeMode, token]);

  const applyPendingNewPosts = useCallback(() => {
    const nextPendingItems = Array.isArray(pendingNewItems) ? pendingNewItems : [];
    const pendingIds = nextPendingItems
      .map((item) => String(item?.id || ''))
      .filter(Boolean);

    for (const id of pendingIds) {
      dismissedPendingIdsRef.current.delete(id);
    }

    setRecentlyAppliedPostIds(pendingIds);
    setItems((prev) => mergeUniquePostsStable(prev, nextPendingItems));
    setPendingNewItems([]);
  }, [pendingNewItems]);

  const loadMore = useCallback(async () => {
    const cursorToUse = nextCursor;
    if (loadingMore || loading || !cursorToUse) return;

    setLoadingMore(true);
    setError('');

    try {
      const result = await getTimeline({
        token,
        limit: initialLimit,
        cursor: cursorToUse,
        mode: safeMode,
      });

      if (!mountedRef.current) return;

      const newItems = Array.isArray(result?.items) ? result.items : [];
      setItems((prev) => mergeUniquePostsStable(prev, newItems));
      setNextCursor(result?.nextCursor || null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || 'No se pudo cargar más contenido.');
    } finally {
      if (!mountedRef.current) return;
      setLoadingMore(false);
    }
  }, [initialLimit, loading, loadingMore, nextCursor, safeMode, token]);

  const dismissPendingNewPosts = useCallback(() => {
    const now = Date.now();

    toSafeArray(pendingNewItems).forEach((item) => {
      const id = String(item?.id || '');
      if (!id) return;
      dismissedPendingIdsRef.current.set(id, now);
    });

    setPendingNewItems([]);
  }, [pendingNewItems]);

  const shouldAcceptRealtimePost = useCallback((post) => {
    const postId = String(post?.id || '');
    if (!post || !postId) return false;

    const authorId = String(post?.author?.id || post?.author?._id || '').trim();
    if (viewerId && authorId && authorId === viewerId) {
      return false;
    }

    return postMatchesFeedMode(post, safeMode, viewerSign);
  }, [safeMode, viewerId, viewerSign]);

  const handleRealtimePostCreated = useCallback((payload = {}) => {
    const post = payload?.post || null;
    const postId = String(post?.id || '');
    if (!shouldAcceptRealtimePost(post) || !postId) return;

    const currentItems = toSafeArray(itemsRef.current);
    const currentPending = toSafeArray(pendingItemsRef.current);

    if (hasPostId(currentItems, postId) || hasPostId(currentPending, postId)) {
      return;
    }

    const matchingOptimistic = currentItems.find((item) => {
      if (!item?._optimistic) return false;

      const sameAuthor = String(item?.author?.id || '') !== ''
        && String(item?.author?.id || '') === String(post?.author?.id || post?.author?._id || '');
      const sameText = String(item?.text || '').trim() === String(post?.text || '').trim();

      return sameAuthor && sameText;
    });

    if (matchingOptimistic?.id) {
      dismissedPendingIdsRef.current.delete(postId);
      setRecentlyAppliedPostIds((prev) => prev.filter((id) => id !== postId));
      setPendingNewItems((prev) => removePostFromList(prev, postId));
      setItems((prev) => reconcileCreatedPost(prev, matchingOptimistic, post));
      return;
    }

    setPendingNewItems((prev) =>
      mergePendingPosts(prev, [post], currentItems, filterDismissedPendingPosts)
    );
  }, [filterDismissedPendingPosts, shouldAcceptRealtimePost]);

  const handleRealtimePostDeleted = useCallback((payload = {}) => {
    const postId = String(payload?.postId || '');
    if (!postId) return;

    dismissedPendingIdsRef.current.delete(postId);
    setRecentlyAppliedPostIds((prev) => prev.filter((id) => id !== postId));
    setPendingNewItems((prev) => removePostFromList(prev, postId));
    setItems((prev) => removePostFromList(prev, postId));
  }, []);

  const handleRealtimeReactionUpdated = useCallback((payload = {}) => {
    const postId = String(payload?.postId || '');
    if (!postId) return;

    if (pendingLikeIdsRef.current.has(postId)) { return; }

    const nextLikeCount = Number(payload?.likeCount || 0);

    setItems((prev) =>
      updatePostInList(prev, postId, (item) => ({
        ...item,
        stats: {
          ...item.stats,
          likeCount: nextLikeCount,
        },
        viewer: {
          ...item.viewer,
          liked: typeof payload?.liked === 'boolean' && viewerId
            ? String(payload?.viewerId || '') === viewerId
              ? !!payload.liked
              : !!item?.viewer?.liked
            : !!item?.viewer?.liked,
        },
      }))
    );

    setPendingNewItems((prev) =>
      updatePostInList(prev, postId, (item) => ({
        ...item,
        stats: {
          ...item.stats,
          likeCount: nextLikeCount,
        },
        viewer: {
          ...item.viewer,
          liked: !!item?.viewer?.liked,
        },
      }))
    );
  }, [viewerId]);

  const handleRealtimeCommentCreated = useCallback((payload = {}) => {
    const postId = String(payload?.postId || '');
    if (!postId) return;

    setItems((prev) =>
      updatePostInList(prev, postId, (item) => ({
        ...item,
        stats: {
          ...item.stats,
          commentCount: Number(item?.stats?.commentCount || 0) + 1,
        },
      }))
    );

    setPendingNewItems((prev) =>
      updatePostInList(prev, postId, (item) => ({
        ...item,
        stats: {
          ...item.stats,
          commentCount: Number(item?.stats?.commentCount || 0) + 1,
        },
      }))
    );
  }, []);

  const handleRealtimeCommentDeleted = useCallback((payload = {}) => {
    const postId = String(payload?.postId || '');
    if (!postId) return;

    setItems((prev) =>
      updatePostInList(prev, postId, (item) => ({
        ...item,
        stats: {
          ...item.stats,
          commentCount: Math.max(0, Number(item?.stats?.commentCount || 0) - 1),
        },
      }))
    );

    setPendingNewItems((prev) =>
      updatePostInList(prev, postId, (item) => ({
        ...item,
        stats: {
          ...item.stats,
          commentCount: Math.max(0, Number(item?.stats?.commentCount || 0) - 1),
        },
      }))
    );
  }, []);

  const submitPost = useCallback(
    async ({ text, uploadId = null, upload = null }) => {
      setCreatingPost(true);
      setError('');

      const optimisticPost = createOptimisticPost({
        text,
        viewer: viewerSnapshot,
        imageUpload: upload || (uploadId ? { id: uploadId } : null),
      });
      creatingPostRef.current = true;

      if (mountedRef.current) {
        setItems((prev) => mergeUniquePostsStable([optimisticPost], prev));
      }

      try {
        const result = await createPost({
          token,
          text,
          uploadId,
        });

        const createdPost = result?.post || null;

        if (!mountedRef.current) return createdPost;

        if (createdPost?.id) {
          const createdId = String(createdPost.id);
          dismissedPendingIdsRef.current.delete(createdId);
          setPendingNewItems((prev) => removePostFromList(prev, createdId));
          setRecentlyAppliedPostIds((prev) => prev.filter((id) => id !== createdId));
        }

        setItems((prev) => reconcileCreatedPost(prev, optimisticPost, createdPost));

        return createdPost;
      } catch (err) {
        if (!mountedRef.current) return null;

        setItems((prev) => removePostFromList(prev, optimisticPost.id));
        setError(err?.message || 'No se pudo publicar.');
        throw err;
      } finally {
        if (!mountedRef.current) return;
        creatingPostRef.current = false;
        setCreatingPost(false);
      }
    },
    [token, viewerSnapshot]
  );

  const removePost = useCallback(
    async (postId) => {
      const safePostId = String(postId || '');
      if (!safePostId) return null;

      setError('');

      const currentItems = toSafeArray(itemsRef.current);
      const existingPost = currentItems.find(
        (item) => String(item?.id || '') === safePostId
      );

      if (existingPost) {
        setItems((prev) => removePostFromList(prev, safePostId));
      }

      try {
        const result = await deletePost({
          token,
          postId: safePostId,
        });

        if (!mountedRef.current) return result;

        dismissedPendingIdsRef.current.delete(safePostId);
        setRecentlyAppliedPostIds((prev) => prev.filter((id) => id !== safePostId));
        setItems((prev) => removePostFromList(prev, safePostId));
        return result;
      } catch (err) {
        if (!mountedRef.current) return null;
        const result = await getTimeline({
          token,
          limit: initialLimit,
          mode: safeMode,
        });
        const nextItems = Array.isArray(result?.items) ? result.items : [];
        setItems((prev) => mergeServerPostsPreservingOptimistic(prev, nextItems));
        setError(err?.message || 'No se pudo eliminar el post.');
        throw err;
      }
    },
    [initialLimit, safeMode, token]
  );

  const toggleLike = useCallback(
    async (post) => {
      const postId = String(post?.id || '');
      if (!postId) return null;
      if (pendingLikeIdsRef.current.has(postId)) return null;

      const wasLiked = !!post?.viewer?.liked;
      const prevLikeCount = Number(post?.stats?.likeCount || 0);
      const optimisticLikeCount = wasLiked
        ? Math.max(0, prevLikeCount - 1)
        : prevLikeCount + 1;

      pendingLikeIdsRef.current.add(postId);

      setItems((prev) =>
        updatePostInList(prev, postId, (item) => ({
          ...item,
          stats: {
            ...item.stats,
            likeCount: optimisticLikeCount,
          },
          viewer: {
            ...item.viewer,
            liked: !wasLiked,
          },
        }))
      );
      setPendingNewItems((prev) =>
        updatePostInList(prev, postId, (item) => ({
          ...item,
          stats: {
            ...item.stats,
            likeCount: optimisticLikeCount,
          },
          viewer: {
            ...item.viewer,
            liked: !wasLiked,
          },
        }))
      );

      try {
        const result = wasLiked
          ? await unlikePost({ token, postId })
          : await likePost({ token, postId });

        if (!mountedRef.current) return result;

        setItems((prev) =>
          updatePostInList(prev, postId, (item) => ({
            ...item,
            stats: {
              ...item.stats,
              likeCount: Number(result?.likeCount || 0),
            },
            viewer: {
              ...item.viewer,
              liked: !!result?.liked,
            },
          }))
        );
        setPendingNewItems((prev) =>
          updatePostInList(prev, postId, (item) => ({
            ...item,
            stats: {
              ...item.stats,
              likeCount: Number(result?.likeCount || 0),
            },
            viewer: {
              ...item.viewer,
              liked: !!result?.liked,
            },
          }))
        );

        return result;
      } catch (err) {
        if (!mountedRef.current) return null;

        setItems((prev) =>
          updatePostInList(prev, postId, (item) => ({
            ...item,
            stats: {
              ...item.stats,
              likeCount: prevLikeCount,
            },
            viewer: {
              ...item.viewer,
              liked: wasLiked,
            },
          }))
        );
        setPendingNewItems((prev) =>
          updatePostInList(prev, postId, (item) => ({
            ...item,
            stats: {
              ...item.stats,
              likeCount: prevLikeCount,
            },
            viewer: {
              ...item.viewer,
              liked: wasLiked,
            },
          }))
        );

        setError(err?.message || 'No se pudo actualizar el like.');
        throw err;
      } finally {
        pendingLikeIdsRef.current.delete(postId);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return undefined;

    const socket = authenticatedSocket;

    socketRef.current = socket;

    const subscribe = () => {
      socket.emit('feed:subscribe', {
        mode: safeMode,
        zodiacSign: viewerSign,
        viewerId,
      });
    };

    socket.on('connect', subscribe);
    socket.on('feed:post_created', handleRealtimePostCreated);
    socket.on('feed:post_deleted', handleRealtimePostDeleted);
    socket.on('feed:post_reaction_updated', handleRealtimeReactionUpdated);
    socket.on('feed:comment_created', handleRealtimeCommentCreated);
    socket.on('feed:comment_deleted', handleRealtimeCommentDeleted);

    if (socket.connected) {
      subscribe();
    }

    return () => {
      try {
        socket.emit('feed:unsubscribe', {
          mode: safeMode,
          zodiacSign: viewerSign,
          viewerId,
        });
      } catch {}

      socket.off('connect', subscribe);
      socket.off('feed:post_created', handleRealtimePostCreated);
      socket.off('feed:post_deleted', handleRealtimePostDeleted);
      socket.off('feed:post_reaction_updated', handleRealtimeReactionUpdated);
      socket.off('feed:comment_created', handleRealtimeCommentCreated);
      socket.off('feed:comment_deleted', handleRealtimeCommentDeleted);

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [
    token,
    safeMode,
    viewerId,
    viewerSign,
    shouldAcceptRealtimePost,
    handleRealtimePostCreated,
    handleRealtimePostDeleted,
    handleRealtimeReactionUpdated,
    handleRealtimeCommentCreated,
    handleRealtimeCommentDeleted,
  ]);

  useEffect(() => {
    initialLoadDoneRef.current = false;
    setItems([]);
    setNextCursor(null);
    setPendingNewItems([]);
    setRecentlyAppliedPostIds([]);
    setError('');
    setLoading(false);
    setLoadingMore(false);
    setRefreshing(false);
    setCheckingNewPosts(false);
    setLastRefreshAt(0);
    itemsRef.current = [];
    pendingItemsRef.current = [];
    dismissedPendingIdsRef.current = new Map();
    lastLoadedModeRef.current = '';
  }, [safeMode]);

  useEffect(() => {
    if (!recentlyAppliedPostIds.length) return undefined;

    const timeout = window.setTimeout(() => {
      setRecentlyAppliedPostIds([]);
    }, 3200);

    return () => window.clearTimeout(timeout);
  }, [recentlyAppliedPostIds]);

  useEffect(() => {
    if (!autoLoad || initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    loadInitial();
  }, [autoLoad, loadInitial]);

  useEffect(() => {
    if (!autoLoad) return undefined;

    const interval = window.setInterval(() => {
      if (loading || loadingMore || refreshing || creatingPostRef.current || checkingNewPosts) return;
      checkForNewPosts();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [
    autoLoad,
    checkForNewPosts,
    checkingNewPosts,
    loading,
    loadingMore,
    refreshing,
  ]);

  return {
    recentlyAppliedPostIds,
    hasRecentlyAppliedPosts,
    lastRefreshAt,
    items,
    loading,
    loadingMore,
    refreshing,
    creatingPost,
    error,
    nextCursor,
    hasMore,
    pendingNewItems,
    pendingNewCount,
    hasPendingNewPosts,
    checkingNewPosts,
    checkForNewPosts,
    applyPendingNewPosts,
    dismissPendingNewPosts,
    loadInitial,
    refresh,
    loadMore,
    submitPost,
    removePost,
    toggleLike,
    setItems,
    setError,
  };
}
